import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { delay, reduce, keyBy, indexOf, find, take, map } from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import { Tooltip, OverlayTrigger, Modal, Button } from 'react-bootstrap';
import Fa from 'react-fontawesome';
import { loadTVFieldMapping, updateTVFieldMapping } from '../../SetupActions';
import ProgressBar from '../../../../components/progress-bar';
import styles from './styles.css';

function LabelRenderer(props) {
  const { value, description, id } = props;
  if (!value) {
    return null;
  }
  let descriptionComp = null;
  if (description && description.length > 0) {
    const tip = (
      <Tooltip id={`data-tv-tip-${id}`}>
        <div>{description}</div>
      </Tooltip>
    );
    descriptionComp = (
      <OverlayTrigger key="tip" trigger={['hover', 'focus']} placement="top" overlay={tip}>
        <Fa name="info-circle" className="text-active" />
      </OverlayTrigger>
    );
  }
  return (
    <span>{value} {descriptionComp}</span>
  );
}
LabelRenderer.propTypes = {
  value: PropTypes.string,
  description: PropTypes.string,
  id: PropTypes.string,
}
LabelRenderer.defaultProps = {
  value: null,
  id: null,
};

function TSLabelRenderer(props) {
  const { data, value } = props;
  if (!value) {
    return null;
  }
  return LabelRenderer({ value, description: data.description, id: data._id });
}
LabelRenderer.propTypes = {
  data: PropTypes.shape({
    _id: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  value: PropTypes.string,
}
LabelRenderer.defaultProps = {
  value: null,
};


function MappingCellRenderer(props) {
  const {
    data, value, tvValue, isSingle, requestToggleMapping,
    context: { submitting, currentTSValueId, currentTVValue },
  } = props;

  let name = isSingle ? 'circle-o' : 'square-o';
  let cls = '';
  let spin = false;

  if (value === true) {
    name = isSingle ? 'check-circle' : 'check-square';
    cls = 'text-success';
  }

  if (submitting && currentTSValueId === data._id && currentTVValue === tvValue) {
    spin = true;
    name = 'spinner';
    cls = 'text-muted';
  }

  const clk = () => {
    if (submitting) {
      return;
    }
    requestToggleMapping(data._id, tvValue)
  };

  return (
    <div style={{ cursor: submitting ? 'not-allowed' : 'pointer' }} className="text-center" onClick={clk}>
      <Fa spin={spin} name={name} className={cls} />
    </div>
  );
}
MappingCellRenderer.propTypes = {
  data: PropTypes.shape({
    _id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }).isRequired,
  tvValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired, 
  value: PropTypes.bool.isRequired,
  isSingle: PropTypes.bool.isRequired,
  requestToggleMapping: PropTypes.func,
  context: PropTypes.shape({
    submitting: PropTypes.bool.isRequired,
    currentTSValueId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    currentTVValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
}
MappingCellRenderer.defaultProps = {
  requestToggleMapping: () => {},
  context: {},
};

function DropdownCellRenderer(props) {
  const {
    data, context: { submitting, currentTSValueId, mappingData },
  } = props;

  let name = 'caret-down';
  let spin = false;

  const cellData = find(mappingData[data._id], { checked: true });

  const isCellSubmitting = currentTSValueId === data._id;

  const label = cellData ? cellData.text : '[excluded]';
  let cls = 'invisible';

  if (isCellSubmitting) {
    name = 'spinner';
    spin = true;
    cls = 'text-muted';
  }

  
  
  return (
    <div style={{ cursor: submitting ? 'not-allowed' : 'pointer' }} className={(submitting || !cellData) ? 'text-muted' : 'text-active'}>
      <u>{LabelRenderer({ value: label, description: cellData ? cellData.description : null, id: data._id })}</u> <Fa spin={spin} name={name} className={cls} />
    </div>
  );
}
DropdownCellRenderer.propTypes = {
  data: PropTypes.shape({
    _id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }).isRequired,
  context: PropTypes.shape({
    mappingData: PropTypes.shape({}),
    submitting: PropTypes.bool.isRequired,
    currentTSValueId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
}
DropdownCellRenderer.defaultProps = {
  requestToggleMapping: () => {},
  context: {},
};

const defaultState = {
  loading: false,
  loaded: false,
  showModal: false,
  tvValues: [],
  tsValues: [],
  columnDefs: [],
  data: {},
  isSinglePickList: true,
  isMuliplePickList: false,
  submitting: false,
  currentTSValueId: null,
  currentTVValue: null,
};

class ValueMappingCellRenderer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...defaultState,
    };
    this.hideModal = this.hideModal.bind(this);
    this.showModal = this.showModal.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.columnValueGetter = this.columnValueGetter.bind(this);
    this.requestToggleMapping = this.requestToggleMapping.bind(this);

    this.checkCellEditable = this.checkCellEditable.bind(this);
    this.cellEditorSelector = this.cellEditorSelector.bind(this);
    this.columnDropdownValueGetter = this.columnDropdownValueGetter.bind(this);
    this.valueSetter = this.valueSetter.bind(this);
  }


  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
  }

  showModal() {
    this.setState({ showModal: true }, () => {
      this.loadData();
    });
  }

  hideModal() {
    this.setState({
      showModal: false,
      loading: false,
      loaded: false,
      submitting: false,
      currentTSValueId: null,
      currentTVValue: null,
    });
  }

  refreshRow(id, flash = false) {
    if (!this.gridApi) {
      return;
    }
    let rowNode = null;
    this.gridApi.forEachNode((node) => {
      if (node.data && node.data._id === id) {
        rowNode = node;
      }
    });
    if (rowNode) {
      this.gridApi.refreshCells({ rowNodes: [rowNode], force: true });
      if (flash) {
        this.gridApi.flashCells({ rowNodes: [rowNode] });
      }
    }
  }

  checkCellEditable(params) {
    if (!params) {
      return null;
    }
    if (this.state.submitting || !this.state.tvValues || this.state.tvValues.length === 0) {
      return false;
    }
    return true;
  }

  
  cellEditorSelector(params) {
    if (!params) {
      return null;
    }
    const { tvValues } = this.state;
    return {
      component: 'agRichSelectCellEditor',
      params: {
        values: [null, ...map(tvValues, 'value')],
        cellRenderer: ({ value }) => {
          const v = find(tvValues, { value });

          const l = value === null ? `[excluded]` : v?.text || v?.label || 'unknown';
          return l;
        },
      },
    };
  }

  valueSetter(params) {
    const { data, newValue, oldValue } = params;
    if (oldValue !== newValue) {
      this.requestToggleMapping(data._id, newValue, newValue === null || newValue === undefined);
      return true;
    }
    return false;
  }

  requestToggleMapping(tsValueId, tvValue, unmapped = false) {
    if (this.state.submitting) {
      return;
    }
    this.setState({
      submitting: true,
      currentTSValueId: tsValueId,
      currentTVValue: tvValue,
    }, () => {
      
      const tsValue = find(this.state.tsValues, { _id: tsValueId });
      if (!tsValue) {
        return;
      }

      const { isSinglePickList } = this.state;

      const newMappings = [
        ...(isSinglePickList ? [] : this.state.data[tsValueId] || []),
      ];
      let cellData = find(newMappings, { value: tvValue });
      if (!cellData) {
        cellData = { value: tvValue, checked: false };
        newMappings.push(cellData);
      }
      cellData.checked = !cellData.checked; // toggle

      const mappings = unmapped ? [] : reduce(newMappings, (items, v) => {
        if (v.checked) {
          items.push(v.value);
        }
        return items;
      }, []);
      this.refreshRow(tsValueId); // update spinner
      this.props.dispatch(updateTVFieldMapping(this.props.source, this.props.data._id, { [tsValue.value]: isSinglePickList ? take(mappings, 1) : mappings }))
        .then(
          (results) => {
            this.setState({
              data: this.initData(results),
              submitting: false,
              currentTSValueId: null,
              currentTVValue: null,
            }, () => {
              delay(() => {
                this.refreshRow(tsValueId, true);
              }, 100);
            });
          },
          () => {
            this.setState({
              submitting: false,
              currentTSValueId: null,
              currentTVValue: null,
            });
          },
        );
      
    });
  }

  columnValueGetter(params) {
    if (params.node.group) {
      // groupping
      return null;
    }
    const { data, context: { mappingData }, colDef: { cellRendererParams: { tvValue } } } = params;
    const tsId = data._id;
    const isMapped = find(mappingData[tsId], { value: tvValue })?.checked === true;
    return isMapped;
  }

  columnDropdownValueGetter(params) {
    if (params.node.group) {
      // groupping
      return null;
    }
    const { data, context: { mappingData } } = params;
    const tsId = data._id;
    return find(mappingData[tsId], { checked: true })?.value // single!!!
  }

  initColumnDefs() {
    const { tvValues, isSinglePickList } = this.state;
    const columnDefs = [
      {
        headerName: 'CRM Value',
        field: 'label',
        suppressMenu: true,
        pinned: 'left',
        width: isSinglePickList ? 250 : 200,
        cellRenderer: TSLabelRenderer,
      },
      ...(isSinglePickList ? [
        {
          field: `_id`,
          headerName: 'TruVoice Field Value',
          valueSetter: this.valueSetter,
          suppressMiniFilter: true,
          suppressSorting: true,
          suppressMenu: true,
          width: 290,
          cellRendererParams: {
          },
          editable: this.checkCellEditable,
          singleClickEdit: true,
          cellRenderer: DropdownCellRenderer,
          cellEditorSelector: this.cellEditorSelector,
          valueGetter: this.columnDropdownValueGetter,
        },
      ] : [
        {
          headerName: 'TruVoice Value',
          field: '_id',
          suppressMenu: true,
          children: reduce(tvValues, (items, tvValue) => {
            items.push({
              headerName: tvValue.text || tvValue.label,
              field: '_id',
              suppressMenu: true,
              cellRenderer: MappingCellRenderer,
              cellRendererParams: {
                tvValue: tvValue.value,
                isSingle: isSinglePickList,
                requestToggleMapping: this.requestToggleMapping,
              },
              valueGetter: this.columnValueGetter,
            });
            return items;
          }, []),
        },
      ]),
    ];
    return columnDefs;
  }

  initData(mappings) {
    const { tvValues, tsValues } = this.state;
    // array of tsFields
    // each collumn is a bool, there will be tvValues.length volumns
    const indexedMappings = keyBy(mappings, 'ts_value');
    const data = reduce(tsValues, (items, tsValue) => {
      const mapping = indexedMappings[tsValue.value] || {};
      const checkedTVValues = mapping.tv_values || [];
      items[tsValue._id] = reduce(tvValues, (itms, tvValue) => {
        itms.push({
          value: tvValue.value,
          text: tvValue.text || tvValue.label,
          description: tvValue.definition, 
          checked: indexOf(checkedTVValues, tvValue.value) >= 0,
        });
        return itms;
      }, []);
      return items;
    }, {});
    return data;
  }


  loadData() {
    this.setState({ ...defaultState, showModal: true, loading: true, loaded: false }, () => {
      const { data, source } = this.props;
      this.props.dispatch(loadTVFieldMapping(source, data._id))
        .then(
          (field) => {
            this.setState({ 
              tsValues: field.values || [], 
              tvValues: field.tv_field?.values || [],
              // isSinglePickList: field.tv_field?.dataType === 20,
              // isMultiplePickList: field.tv_field?.dataType === 21,
              isSinglePickList: true,
              isMultiplePickList: false,
            }, () => {
              this.setState({
                columnDefs: this.initColumnDefs(),
                data: this.initData(field.mappings),
                loaded: true,
                loading: false,
              });
            });
          },
          () => {
            this.setState({ loading: false, loaded: false });
          },
        );
    });
  }

  renderTable() {
    const {
      tsValues, data, columnDefs,
      submitting, currentTSValueId, currentTVValue,
    } = this.state;

    return (
      <div className={`ag-theme-alpine ${styles.table}`} style={{ height: '400px', width: '100%' }}>
        <AgGridReact
          reactNext
          enableColResize
          enableSorting
          animateRows
          columnDefs={columnDefs}
          getRowId={(params) => params.data?._id}
          onGridReady={this.onGridReady}
          rowData={tsValues}
          context={{
            mappingData: data,
            submitting,
            currentTSValueId,
            currentTVValue,
          }}
        />
      </div>
    );
  }

  renderModal() {
    const { loading, loaded, isSinglePickList } = this.state;

    return (
      <Modal bsSize={isSinglePickList ? null : 'lg'} show={this.state.showModal} onHide={this.hideModal}>
        <Modal.Header closeButton>
          <h4>Edit Value Mapping</h4>
        </Modal.Header>
        <Modal.Body>
          <div>
            {loading && !loaded && <ProgressBar />}
            {!loading && loaded && this.renderTable()}
          </div>
        </Modal.Body>
        <Modal.Footer className="clearfix">
          <Button className="pull-right" onClick={this.hideModal}>Done</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    const { data } = this.props;
    if (!data) {
      return null;
    }
    if (data.accessible === false) {
      return null;
    }
    const isPickList = data.tv_field?.dataType === 20;
    const isMultiPickList = data.tv_field?.dataType === 21;
    if (!isMultiPickList && !isPickList) {
      return null;
    }
    const { loading } = this.state;
    return (
      <span>
        <Button disabled={loading} bsSize="xs" bsStyle="default" onClick={this.showModal}>
          Edit Mappings
        </Button>
        {this.renderModal()}
      </span>
    );
  }
}

ValueMappingCellRenderer.propTypes = {
  dispatch: PropTypes.func.isRequired,
  data: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    tv_field: PropTypes.shape({
      dataType: PropTypes.number,
    }),
    definition: PropTypes.string,
    name: PropTypes.string,
    active: PropTypes.bool,
    accessible: PropTypes.bool,
  }).isRequired,
  disabled: PropTypes.bool,
  source: PropTypes.string.isRequired,
}
ValueMappingCellRenderer.defaultProps = {
  disabled: false,
}

function mapStateToProps() {
  return {
  };
}

export default connect(mapStateToProps, null, null, { forwardRef: true })(ValueMappingCellRenderer);
